import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type PrizeWeightSectionKey = 'dailyPrizeWeights' | 'remainingPrizeWeights';

export interface PrizeWeightSection {
  key: PrizeWeightSectionKey;
  label: string;
}

export interface PrizeWeightRow {
  id: string;
  /** CMS `categories` document _id, once resolved — see PrizeWeightsFilterComponent. */
  categoryRef: string | null;
  categoryName: string;
  weight: number;
}

/** Matches the "Daily Prize Weights" / "Remaining Prize Weights" sections on the Generate panel. */
export const PRIZE_WEIGHT_SECTIONS: PrizeWeightSection[] = [
  { key: 'dailyPrizeWeights', label: 'Daily Prize Weights' },
  { key: 'remainingPrizeWeights', label: 'Remaining Prize Weights' },
];

/**
 * Pre-filled the first time each weight table loads. There's no filter-persistence service yet
 * (separate ticket) — until saved filters can be loaded, every session starts from these
 * product-specified defaults.
 */
const DEFAULT_ROWS_BY_SECTION: Record<PrizeWeightSectionKey, ReadonlyArray<Omit<PrizeWeightRow, 'id'>>> = {
  dailyPrizeWeights: [
    { categoryRef: null, categoryName: 'Small Plant', weight: 68 },
    { categoryRef: null, categoryName: 'Ground Cover', weight: 22 },
    { categoryRef: null, categoryName: 'Stool', weight: 5 },
    { categoryRef: null, categoryName: 'Chair Sets', weight: 2.5 },
    { categoryRef: null, categoryName: 'Medium Plants', weight: 2.5 },
  ],
  remainingPrizeWeights: [
    { categoryRef: null, categoryName: 'Medium Plant', weight: 11 },
    { categoryRef: null, categoryName: 'Benches', weight: 11 },
    { categoryRef: null, categoryName: 'Chair Sets', weight: 11 },
    { categoryRef: null, categoryName: 'Chaise Lounge', weight: 11 },
    { categoryRef: null, categoryName: 'Coffee Table', weight: 11 },
    { categoryRef: null, categoryName: 'Side Table', weight: 11 },
    { categoryRef: null, categoryName: 'Sofa', weight: 11 },
    { categoryRef: null, categoryName: 'Dining Table', weight: 5 },
    { categoryRef: null, categoryName: 'Dining Chairs', weight: 5 },
    { categoryRef: null, categoryName: 'Aquatic Plants', weight: 5 },
    { categoryRef: null, categoryName: 'Fire Pit', weight: 5 },
    { categoryRef: null, categoryName: 'Large Plant', weight: 1.5 },
    { categoryRef: null, categoryName: 'Climbers', weight: 1.5 },
  ],
};

const REQUIRED_TOTAL = 100;

let nextRowId = 0;
function createRowId(section: PrizeWeightSectionKey): string {
  nextRowId += 1;
  return `${section}-row-${nextRowId}`;
}

export function buildDefaultPrizeWeightRows(section: PrizeWeightSectionKey): PrizeWeightRow[] {
  return DEFAULT_ROWS_BY_SECTION[section].map((row) => ({ ...row, id: createRowId(section) }));
}

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Reads the current total (%) allocated within each prize-weight filter section, and whether
 * each one is balanced (sums to 100%) and has at least one category.
 *
 * Both Daily Prize Weights and Remaining Prize Weights are backed by real state: each section
 * has its own BehaviorSubject in `rowsBySection`, written to by PrizeWeightsFilterComponent (one
 * instance per section) and read by both the component (to render its table + live total) and
 * the submit guards in GeneratePrizesPanelComponent (via getSectionTotal/getCategoryCount below).
 * Each section's total/category-count is tracked and validated independently of the other.
 */
@Injectable({
  providedIn: 'root',
})
export class PrizeWeightFiltersStateService {
  private readonly rowsBySection: Record<PrizeWeightSectionKey, BehaviorSubject<PrizeWeightRow[]>> = {
    dailyPrizeWeights: new BehaviorSubject<PrizeWeightRow[]>(buildDefaultPrizeWeightRows('dailyPrizeWeights')),
    remainingPrizeWeights: new BehaviorSubject<PrizeWeightRow[]>(buildDefaultPrizeWeightRows('remainingPrizeWeights')),
  };

  getRows(section: PrizeWeightSectionKey): PrizeWeightRow[] {
    return this.rowsBySection[section].value;
  }

  watchRows(section: PrizeWeightSectionKey): Observable<PrizeWeightRow[]> {
    return this.rowsBySection[section].asObservable();
  }

  setRows(section: PrizeWeightSectionKey, rows: PrizeWeightRow[]): void {
    this.rowsBySection[section].next(rows);
  }

  getSectionTotal(section: PrizeWeightSectionKey): number {
    return roundToTwoDecimals(this.rowsBySection[section].value.reduce((sum, row) => sum + (row.weight || 0), 0));
  }

  isSectionBalanced(section: PrizeWeightSectionKey): boolean {
    return this.getSectionTotal(section) === REQUIRED_TOTAL;
  }

  getUnbalancedSections(): PrizeWeightSection[] {
    return PRIZE_WEIGHT_SECTIONS.filter((section) => !this.isSectionBalanced(section.key));
  }

  getCategoryCount(section: PrizeWeightSectionKey): number {
    return this.rowsBySection[section].value.length;
  }

  hasAtLeastOneCategory(section: PrizeWeightSectionKey): boolean {
    return this.getCategoryCount(section) > 0;
  }

  /**
   * Sections missing at least one category. Per the ticket's default (spec wording was
   * ambiguous on "at least 1 category" total-vs-per-table): requires at least 1 category in
   * EACH table, mirroring the per-section 100%-total check above. Confirm with product/ticket
   * owner if "at least 1 total across both tables" was actually intended instead.
   */
  getSectionsMissingCategories(): PrizeWeightSection[] {
    return PRIZE_WEIGHT_SECTIONS.filter((section) => !this.hasAtLeastOneCategory(section.key));
  }
}
